import Field from '@components/field';
import Group from '@components/group';
import Input from '@components/input';
import Popup from '@components/popup';
import Preloader from '@components/preloader';
import RadioButton from '@components/radio-button';
import Row from '@components/row';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, FC } from 'react';

import { useStore } from 'hooks/useStore';

import { FilterSummary } from './FilterSummary';
import { Footer } from './Footer';
import { SubsSettings } from './SubsSettings';
import { SubsToggle } from './SubsToggle';
import { Title } from './Title/Title';
import { Warnings } from './Warnings';

import css from './FilterPopup.module.css';

const FilterPopup: FC = () => {
  const {
    ui: { popups },
    app: { i18n },
    savedFilters: { popup, closePopup },
  } = useStore();

  if (!popups.filterPopup?.isOpened || !popup) return null;

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    if (popup.isNameInvalid) {
      popup.setIsNameInvalid(false);
    }

    popup.name.setValue(event.target.value);
  };

  return (
    <Popup closeOnEscape closeOnOverlayClick onClose={closePopup}>
      {popup.isValidatingFilter ? (
        <div className={css.preloader}>
          <Preloader width={64} />
        </div>
      ) : (
        <>
          <Popup.Header>
            <Title />
          </Popup.Header>

          <Popup.Content>
            <FilterSummary />
          </Popup.Content>

          <Popup.Content>
            <Row className={css.name} label={i18n.filterPopup.name}>
              <Field size="xxlarge">
                <Input error={popup.nameErrorDescription} value={popup.name.value} onChange={handleChangeName} />
              </Field>
            </Row>

            <Row className={css.availabilityRow} label={i18n.filterPopup.available}>
              <Group displayColumn>
                <Field>
                  <RadioButton
                    className={css.radio}
                    inputProps={{
                      checked: popup.isCollective.value,
                    }}
                    onClick={() => popup.isCollective.setData(true)}
                  >
                    {i18n.filterPopup.toAll}
                  </RadioButton>
                </Field>

                <Field>
                  <RadioButton
                    className={css.radio}
                    inputProps={{
                      checked: !popup.isCollective.value,
                    }}
                    onClick={() => popup.isCollective.setData(false)}
                  >
                    {i18n.filterPopup.onlyMe}
                  </RadioButton>
                </Field>
              </Group>
            </Row>
          </Popup.Content>

          <SubsToggle isChecked={popup.hasSubscription.value} onCheck={popup.hasSubscription.setValueFromCheckbox} />

          <div className={css.divider} />

          <Popup.Content>{popup.hasSubscription.value && <SubsSettings />}</Popup.Content>

          <div className={css.separator}></div>

          <Warnings />

          <Popup.Footer className={css.footer}>
            <Footer />
          </Popup.Footer>
        </>
      )}
    </Popup>
  );
};

const connected = observer(FilterPopup);

export { connected as FilterPopup };
